import React, { useEffect, useMemo, useRef, useState } from 'react';

const MAX_VISIBLE_ROWS = 3;
const TOGGLE_LABEL = 'Показать больше';

function ProfileTagsCollapse({ tags, className = 'profile-tags' }) {
  const measureContainerRef = useRef(null);
  const measureItemRefs = useRef([]);
  const measureToggleRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(tags.length);
  const [hasOverflow, setHasOverflow] = useState(false);

  const tagsKey = useMemo(() => tags.join('||'), [tags]);
  const tagCount = tags.length;

  useEffect(() => {
    setIsExpanded(false);
    measureItemRefs.current = measureItemRefs.current.slice(0, tagCount);
  }, [tagsKey, tagCount]);

  useEffect(() => {
    const measure = () => {
      const container = measureContainerRef.current;
      const items = measureItemRefs.current.slice(0, tagCount).filter(Boolean);

      if (!container || items.length === 0) {
        setVisibleCount(tagCount);
        setHasOverflow(false);
        return;
      }

      const uniqueRowTops = [...new Set(items.map((item) => Math.round(item.offsetTop)))];

      if (uniqueRowTops.length <= MAX_VISIBLE_ROWS) {
        setVisibleCount(tagCount);
        setHasOverflow(false);
        return;
      }

      const thirdRowTop = uniqueRowTops[MAX_VISIBLE_ROWS - 1];
      const firstHiddenIndex = items.findIndex((item) => Math.round(item.offsetTop) > thirdRowTop);
      const toggleWidth = measureToggleRef.current?.offsetWidth || 0;
      const gap =
        parseFloat(getComputedStyle(container).columnGap || '') ||
        parseFloat(getComputedStyle(container).gap || '') ||
        0;
      const containerWidth = container.clientWidth;

      let nextVisibleCount = firstHiddenIndex === -1 ? tagCount : firstHiddenIndex;

      while (nextVisibleCount > 0) {
        const lastVisibleItem = items[nextVisibleCount - 1];
        const isOnThirdRow = Math.round(lastVisibleItem.offsetTop) === thirdRowTop;
        const usedWidthOnThirdRow = isOnThirdRow
          ? lastVisibleItem.offsetLeft + lastVisibleItem.offsetWidth + gap
          : 0;

        if (usedWidthOnThirdRow + toggleWidth <= containerWidth + 1) {
          break;
        }

        nextVisibleCount -= 1;
      }

      setVisibleCount(nextVisibleCount);
      setHasOverflow(true);
    };

    const handle = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(handle);
      window.removeEventListener('resize', measure);
    };
  }, [tagCount, tags, isExpanded]);

  const shownTags = isExpanded || !hasOverflow ? tags : tags.slice(0, visibleCount);

  return (
    <div className="profile-tags-wrap">
      <div className={className}>
        {shownTags.map((tag, index) => (
          <span key={`${tag}-${index}`}>{tag}</span>
        ))}
        {!isExpanded && hasOverflow && (
          <button
            type="button"
            className="profile-tags__toggle"
            onClick={() => setIsExpanded(true)}
          >
            {TOGGLE_LABEL}
          </button>
        )}
      </div>

      <div
        ref={measureContainerRef}
        className={`${className} profile-tags--measure`}
        aria-hidden="true"
      >
        {tags.map((tag, index) => (
          <span
            key={`measure-${tag}-${index}`}
            ref={(element) => {
              measureItemRefs.current[index] = element;
            }}
          >
            {tag}
          </span>
        ))}
        <button
          type="button"
          className="profile-tags__toggle"
          ref={measureToggleRef}
          tabIndex={-1}
        >
          {TOGGLE_LABEL}
        </button>
      </div>
    </div>
  );
}

export default ProfileTagsCollapse;
